import { useState } from 'react';
import { 
    Trash2, 
    Pen, 
    Copy,
    ChevronDown, 
    ChevronUp,
    Trophy,
    Target,
    List
} from 'lucide-react';

type PassEntryProps = {
    skillsList: any[];
    pass: any;
    setEditing: (type: string, pass: any) => void;
    duplicate: (type: string, pass: any) => void;
    remove: (id: string) => void;
};

const PassEntry: React.FC<PassEntryProps> = ({ skillsList, pass, setEditing, duplicate, remove }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const thisPass = {
        ...pass,
        skills: pass.routine.skills.map((skill: any) => {
            return skillsList.filter((value) => value.id === skill)[0];
        })
    };

    const getStatusConfig = (status: string) => {
        return status === 'active' 
            ? { color: '#10b981', bgColor: 'bg-green-50', textColor: 'text-green-700', label: 'Active' }
            : { color: '#ef4444', bgColor: 'bg-red-50', textColor: 'text-red-700', label: 'Inactive' };
    };

    const statusConfig = getStatusConfig(pass.status);

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white relative">
                <div className="absolute inset-0 bg-white opacity-10"></div>
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Target className="w-4 h-4" />
                        <div>
                            <h3 className="font-bold text-lg">
                                {pass.name.length > 45 ? pass.name.slice(0, 45) + '...' : pass.name}
                            </h3>
                            <div 
                                className="w-3 h-3 rounded-full ml-3" 
                                style={{ backgroundColor: statusConfig.color }}
                            ></div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setEditing('dmt', pass)}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Edit"
                        >
                            <Pen className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                        </button>

                        <button
                            onClick={() => duplicate('dmt', pass)}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Duplicate"
                        >
                            <Copy className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                        </button>

                        <button
                            onClick={() => remove(pass.id)}
                            className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4 text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Pass Name */}
                {/* <div className="mb-4">
                    <div className="flex items-center justify-between">
                        <h4 className="text-xl font-semibold text-gray-800 leading-tight">
                            {pass.name.length > 45 ? pass.name.slice(0, 45) + '...' : pass.name}
                        </h4>
                        <div 
                            className="w-3 h-3 rounded-full ml-3" 
                            style={{ backgroundColor: statusConfig.color }}
                        ></div>
                    </div>
                </div> */}

                {/* Quick Stats */}
                {!isExpanded && (
                    <div className="grid grid-cols-1 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-sm text-gray-500">Total Difficulty</div>
                            <div className="text-lg font-bold text-gray-800">
                                {pass.routine.difficulty}
                            </div>
                        </div>
                    </div>
                )}

                {/* Expanded Details */}
                <div className={`transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        {/* Difficulty Metrics */}
                        <div className="grid grid-cols-1 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                <Trophy className="w-5 h-5 text-blue-500" />
                                <div>
                                    <div className="text-sm text-blue-600 font-medium">Total Difficulty</div>
                                    <div className="text-lg font-bold text-blue-800">
                                        {pass.routine.difficulty}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Skills Table */}
                        <div className="bg-indigo-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-3">
                                <List className="w-5 h-5 text-indigo-600" />
                                <span className="font-semibold text-indigo-800 text-sm">Skills ({thisPass.skills.length})</span>
                            </div>
                            <div className="bg-white rounded-lg overflow-hidden border border-indigo-100">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-indigo-100">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">#</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">Skill</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">Difficulty</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-indigo-50">
                                            {thisPass.skills.map((skill: any, index: number) => (
                                                <tr key={index} className="hover:bg-indigo-25">
                                                    <td className="px-3 py-2 text-sm text-indigo-800 font-medium">{index + 1}</td>
                                                    <td className="px-3 py-2 text-sm text-indigo-900">{skill.name}</td>
                                                    <td className="px-3 py-2 text-sm text-indigo-800 font-medium">{skill.difficulty}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toggle Button */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 font-medium text-gray-700"
                >
                    <span className="text-base">{isExpanded ? 'Show Less' : 'Show More'}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
};

export default PassEntry;